<template>
  <section class="about" ref="sectionRef">
    <div class="about__container">
      <div class="about__header" ref="headerRef">
        <h2 class="about__watermark" ref="watermarkRef">{{ $t('page.about.watermark') }}</h2>
        <h2 class="about__title sr-only">{{ $t('page.about.title') }}</h2>
      </div>

      <div class="bento relative z-10" ref="bentoRef">
        <div class="bento__card bento__card--hero">
          <HeroCard />
        </div>
        <div class="bento__card bento__card--location">
          <LocationCard />
        </div>
        <div class="bento__card bento__card--experience">
          <ExperienceCard />
        </div>
        <div class="bento__card bento__card--quote">
          <QuoteCard />
        </div>

        <div class="bento__card bento__card--stack">
          <StackCard />
        </div>
        <div class="bento__card bento__card--status">
          <AvailabilityCard />
        </div>

        <div class="bento__card bento__card--languages">
          <LanguagesCard />
        </div>
        <div class="bento__card bento__card--neuro">
          <NeurodivergentCard />
        </div>

        <div
          class="bento__card bento__card--see-more"
          :class="{ 'is-expanded': showPersonalCards }"
          @click="togglePersonalCards"
        >
          <div class="see-more-card-glass">
            <div class="see-more-content">
              <div class="see-more-icon-wrapper">
                <ArrowDownDoubleIcon class="see-more-icon" />
              </div>
              <div class="see-more-text-wrapper">
                <span class="see-more-label">
                  {{ $t('page.about.seeMore.label') }}
                </span>
                <h4 class="see-more-title">
                  {{ $t('page.about.seeMore.title') }}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Transition name="modal-fade">
        <div
          v-if="showPersonalCards"
          class="personal-modal-backdrop"
          @click.self="togglePersonalCards"
        >
          <div class="personal-modal-content" data-lenis-prevent>
            <button class="personal-modal-close" @click="togglePersonalCards">
              <Cancel01Icon class="w-6 h-6" />
            </button>

            <div class="bento--personal-grid">
              <div class="bento__card bento__card--story">
                <div class="story-card">
                  <p class="story-card__text" v-html="$t('page.about.story')"></p>
                </div>
              </div>

              <div class="bento__card bento__card--hobbies">
                <HobbiesCard />
              </div>
              <div class="bento__card bento__card--reading">
                <ReadingCard />
              </div>

              <div class="bento__card bento__card--family">
                <FamilyCard />
              </div>
              <div class="bento__card bento__card--collage">
                <MomentsCard />
              </div>
              <div class="bento__card bento__card--setup">
                <SetupCard />
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { ArrowDownDoubleIcon, ArrowUpDoubleIcon, ViewIcon, Cancel01Icon } from 'hugeicons-vue'
import StackCard from '@/components/landingFolio/StackCard.vue'
import QuoteCard from '@/components/landingFolio/QuoteCard.vue'
import ReadingCard from '@/components/landingFolio/ReadingCard.vue'
import NeurodivergentCard from '@/components/landingFolio/NeurodivergentCard.vue'
import MomentsCard from '@/components/landingFolio/MomentsCard.vue'
import AvailabilityCard from '@/components/landingFolio/AvailabilityCard.vue'
import SetupCard from '@/components/landingFolio/SetupCard.vue'
import FamilyCard from '@/components/landingFolio/FamilyCard.vue'
import HeroCard from '@/components/landingFolio/HeroCardV2.vue'
import LanguagesCard from '@/components/landingFolio/LanguagesCard.vue'
import ExperienceCard from '@/components/landingFolio/ExperienceCard.vue'
import LocationCard from '@/components/landingFolio/LocationCard.vue'
import HobbiesCard from '@/components/landingFolio/HobbiesCard.vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// Template refs for scroll animations
const sectionRef = ref(null)
const headerRef = ref(null)
const watermarkRef = ref(null)
const bentoRef = ref(null)
// Show/hide personal cards
const showPersonalCards = ref(false)

// Lock body scroll when modal is open
watch(showPersonalCards, (newValue) => {
  if (newValue) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.paddingRight = `${scrollbarWidth}px`
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.documentElement.classList.add('lenis-stopped')
    window.lenis?.stop() // Stop Lenis scroll
  } else {
    document.body.style.paddingRight = ''
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''
    document.documentElement.classList.remove('lenis-stopped')
    window.lenis?.start() // Resume Lenis scroll
  }
})

const togglePersonalCards = () => {
  showPersonalCards.value = !showPersonalCards.value
}

// ============================================================================
// SCROLL ANIMATIONS (GSAP)
// ============================================================================

let scrollAnimationFrame = null

// Animación del header - blur + translate desde abajo (ajustado para ser más sutil)
const animateHeader = (element) => {
  if (!element) return

  const rect = element.getBoundingClientRect()
  const windowHeight = window.innerHeight

  // Zona de entrada (desde abajo) - más rápida para aparecer
  const enterStart = windowHeight * 1.1
  const enterEnd = windowHeight * 0.85

  // Zona de salida (por arriba) - solo cuando casi sale
  const exitStart = windowHeight * 0.15
  const exitEnd = -rect.height * 0.5

  let opacity = 1
  let blur = 0
  let y = 0

  // Entrando desde abajo (el elemento está por debajo de la zona visible)
  if (rect.top > enterEnd) {
    const progress = Math.min(1, Math.max(0, (enterStart - rect.top) / (enterStart - enterEnd)))
    opacity = progress
    blur = (1 - progress) * 8
    y = (1 - progress) * 30
  }
  // Saliendo por arriba (el elemento está por encima de la zona segura)
  else if (rect.top < exitStart) {
    const progress = Math.min(1, Math.max(0, (exitStart - rect.top) / (exitStart - exitEnd)))
    opacity = Math.max(0.05, 1 - progress * 0.95)
    y = progress * -20
  }
  // En zona visible completa - sin efectos
  else {
    opacity = 1
    y = 0
  }

  gsap.set(element, {
    opacity,
    filter: `blur(${blur}px)`,
    y,
    force3D: true,
  })
}

// Animación de cards - escala
const animateCard = (element) => {
  if (!element) return

  const rect = element.getBoundingClientRect()
  const windowHeight = window.innerHeight

  // Zona de entrada (desde abajo)
  const enterStart = windowHeight
  const enterEnd = windowHeight * 0.7

  // Zona de salida (por arriba)
  const exitStart = windowHeight * 0.2
  const exitEnd = -rect.height

  let opacity = 1
  let scale = 1

  // Entrando desde abajo - escala de pequeño a normal
  if (rect.top > enterEnd) {
    const progress = Math.min(1, Math.max(0, (enterStart - rect.top) / (enterStart - enterEnd)))
    opacity = progress
    scale = 0.85 + progress * 0.15 // De 0.85 a 1
  }
  // Saliendo por arriba - escala de normal a pequeño
  else if (rect.top < exitStart) {
    const progress = Math.min(1, Math.max(0, (exitStart - rect.top) / (exitStart - exitEnd)))
    opacity = 1 - progress
    scale = 1 - progress * 0.15 // De 1 a 0.85
  }

  element.style.opacity = opacity
  element.style.transform = `scale(${scale})`
}

const animateCards = () => {
  if (!bentoRef.value) return

  // Animar las cards del bento grid
  const cards = bentoRef.value.querySelectorAll('.bento__card')
  cards.forEach((card) => {
    animateCard(card)
  })
}

const handleScrollAnimation = () => {
  // Animar header con blur + translate
  animateHeader(headerRef.value)

  // Animar cards con escala
  animateCards()

  scrollAnimationFrame = requestAnimationFrame(handleScrollAnimation)
}

const setupScrollAnimations = () => {
  scrollAnimationFrame = requestAnimationFrame(handleScrollAnimation)

  // Watermark Parallax & Fade
  if (watermarkRef.value && sectionRef.value) {
    gsap.fromTo(
      watermarkRef.value,
      {
        y: -60,
        opacity: 0.1,
        scale: 1,
      },
      {
        scrollTrigger: {
          trigger: sectionRef.value,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
        y: 160, // Deep parallax - moves down slower than scroll
        opacity: 70, // Fades out completely
        scale: 0.6, // Tucks in slightly
        ease: 'none',
      }
    )
  }
}

onMounted(() => {
  // Setup scroll animations
  setupScrollAnimations()
})

onUnmounted(() => {
  // Clean up scroll animations
  if (scrollAnimationFrame) cancelAnimationFrame(scrollAnimationFrame)
})
</script>

<style>
.about {
  @apply relative py-32 overflow-hidden;
  background: var(--color-background);
  /* Prevenir saltos de scroll cuando el contenido cambia */
  overflow-anchor: none;
}

.story-highlight {
  @apply text-[var(--color-text-primary)];
}

.about__container {
  @apply max-w-7xl mx-auto px-6;
  /* Ancla de scroll para mantener posición */
  overflow-anchor: auto;
}

/* Header */
.about__header {
  @apply text-center relative h-32 flex items-center justify-center;
}

.about__title {
  @apply text-4xl md:text-5xl lg:text-6xl font-bold leading-tight;
  color: var(--color-text-primary);
}

.about__title-accent {
  background: linear-gradient(135deg, var(--color-electric-purple) 0%, #818cf8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Bento Grid */
/* Bento Grid - Mobile First */
.bento {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

/* Mobile: Side-by-side cards (2 columns layout) */
.bento__card--location,
.bento__card--experience,
.bento__card--fuel,
.bento__card--timezone {
  @apply col-span-2 md:col-span-1 lg:col-span-1;
}

.bento__card--hobbies {
  @apply col-span-4 lg:col-span-1;
}

.bento__card--neuro {
  @apply col-span-4 md:col-span-2 lg:col-span-1 h-[300px] lg:h-auto;
}

.bento__card--devotional,
.bento__card--languages {
  @apply col-span-2 md:col-span-2 lg:col-span-1;
}

.bento__card--status {
  @apply col-span-2 md:col-span-2 lg:col-span-1;
}

.bento__card--hero {
  @apply col-span-4 md:col-span-4 lg:col-span-2;
}

.bento__card--setup {
  @apply col-span-4;
}

/* Personal Cards Grid - Mobile First */
.bento--personal {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: minmax(140px, auto);
  gap: 12px;
  margin-top: 16px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transform: translateY(-20px);
  transition:
    max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-anchor: none;
}

.bento__card--story {
  @apply col-span-4 lg:col-span-2;
}

.bento__card--reading {
  @apply col-span-4 lg:col-span-1;
}

.bento__card--collage {
  @apply col-span-4 lg:col-span-2;
}

.bento__card--family {
  @apply col-span-4 lg:col-span-2;
}

/* Base Card */
.bento__card {
  @apply relative rounded-[3rem] overflow-hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  transition:
    border-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  corner-shape: squircle;
  will-change: transform, opacity, filter;
}

/* .bento__card:hover:not(.bento__card--timezone) {
  border-color: var(--color-electric-purple);
  box-shadow: 0 25px 50px -12px rgba(139, 92, 246, 0.15);
} */

/* Timezone cards - no hover effect, custom shadows */
.bento__card--timezone {
  border: none;
}

/* Header scroll animation */
.about__header {
  will-change: transform, opacity, filter;
}

/* See More Card - Full width horizontal */
.bento__card--see-more {
  @apply col-span-4 md:col-span-2 lg:col-span-1;
  cursor: pointer;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  transition:
    background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Expanded state specifically for the container */
.bento__card--see-more.is-expanded {
  background: var(--color-surface-elevated);
  border-color: var(--color-electric-purple);
  box-shadow: 0 0 30px -10px rgba(124, 58, 237, 0.15);
}

.bento__card--see-more:hover {
  border-color: var(--color-electric-purple);
}

.see-more-card-glass {
  @apply h-full w-full flex flex-col items-center justify-center p-6 gap-4 text-center;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.01) 0%,
    rgba(124, 58, 237, 0.02) 50%,
    rgba(255, 255, 255, 0.01) 100%
  );
}

.see-more-content {
  @apply flex flex-col items-center gap-3;
}

.see-more-icon-wrapper {
  @apply w-14 h-14 rounded-full flex items-center justify-center mb-1;
  background: var(--color-surface-elevated);
  color: var(--color-electric-purple);
  transition: all 0.4s ease;
}

.bento__card--see-more:hover .see-more-icon-wrapper {
  background: var(--color-electric-purple);
  color: white;
}

.see-more-icon {
  @apply w-7 h-7;
  /* Removed transition and transform */
}

/* Keyframes removed */

.bento__card--see-more .see-more-icon {
  animation: none;
}

.see-more-text-wrapper {
  @apply flex flex-col items-center;
}

.see-more-label {
  @apply text-[10px] font-bold tracking-[0.15em] uppercase mb-1;
  color: var(--color-text-muted);
  opacity: 0.8;
}

.see-more-title {
  @apply text-lg font-bold leading-tight;
  color: var(--color-text-primary);
}

/* Removed hover text gradient */
/* Hide the secondary button in this compact view as the whole card is clickable/hoverable */
.see-more-action {
  @apply hidden; /* We simplify the UI for the small card */
}

.action-icon {
  @apply w-5 h-5;
  color: var(--color-electric-purple);
}

.experience-card {
  @apply text-center relative;
}

.experience-card__number {
  @apply text-6xl font-black leading-none;
  background: linear-gradient(135deg, var(--color-electric-purple) 0%, #818cf8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.experience-card__plus {
  @apply text-4xl font-bold;
  color: var(--color-electric-purple);
}

.experience-card__label {
  @apply text-sm mt-2;
  color: var(--color-text-secondary);
}

/* Location Card */
.bento__card--location {
  @apply overflow-hidden;
}

.location-card {
  @apply h-full flex flex-col;
}

.location-card__map {
  @apply flex-1 relative min-h-[80px];
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(129, 140, 248, 0.05) 100%);
}

.map-dot {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full;
  background: var(--color-electric-purple);
}

.map-pulse {
  @apply absolute inset-0 rounded-full;
  background: var(--color-electric-purple);
  animation: map-pulse 2s infinite;
}

@keyframes map-pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(3);
    opacity: 0;
  }
}

.location-card__info {
  @apply p-4;
}

.location-card__label {
  @apply text-xs uppercase tracking-wider block mb-1;
  color: var(--color-text-muted);
}

.location-card__city {
  @apply text-lg font-bold;
  color: var(--color-text-primary);
}

.location-card__country {
  @apply text-sm;
  color: var(--color-text-secondary);
}

/* Quote Card */
.bento__card--quote {
  @apply flex items-center col-span-4 md:col-span-2 lg:col-span-2;
}

/* Stack Card - Infinite Carousel */
.bento__card--stack {
  @apply flex items-center col-span-4 md:col-span-4 lg:col-span-2;
  overflow: hidden;
}

/* Projects Card */
.bento__card--projects {
  @apply flex items-center justify-center;
}

.projects-card {
  @apply text-center p-4;
}

.projects-card__number {
  @apply text-4xl font-bold block;
  color: var(--color-text-primary);
}

.projects-card__label {
  @apply text-sm mb-3;
  color: var(--color-text-secondary);
}

.projects-card__bar {
  @apply w-full h-1.5 rounded-full overflow-hidden;
  background: var(--color-surface-elevated);
}

.bar-fill {
  @apply h-full rounded-full;
  width: 85%;
  background: linear-gradient(90deg, var(--color-electric-purple) 0%, #818cf8 100%);
}

/* Status Card - Now uses AvailabilityCard component */
.bento__card--status {
  @apply overflow-hidden;
}

/* Hobbies Card - Now using HobbiesCard component */
/* Styles removed as they are now in HobbiesCard.vue */

/* Story Card */
.bento__card--story {
  @apply flex items-center;
}

.story-card {
  @apply p-6 relative h-full flex items-center;
}

.story-card__text {
  @apply text-base leading-relaxed relative z-10;
  color: var(--color-text-secondary);
}

/* Collage Card */
.bento__card--collage {
  @apply p-0;
}

/* Personal Cards Grid */
/* Personal Modal Styles */
.personal-modal-backdrop {
  @apply fixed inset-0 z-[90] flex items-center justify-center p-2 sm:p-4 lg:p-8;
  background: rgba(10, 10, 15, 0.6); /* surface dimmed */
  backdrop-filter: blur(8px);
}

.personal-modal-content {
  @apply relative w-full max-w-7xl max-h-[90vh] overflow-y-auto rounded-[2rem] sm:rounded-[3rem] lg:rounded-[5rem] p-4 sm:p-6 lg:p-8;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  /* Custom scrollbar for the modal */
  scrollbar-width: thin;
  scrollbar-color: var(--color-surface-elevated) transparent;
  corner-shape: squircle;
}

.personal-modal-close {
  @apply fixed top-4 right-4 z-50 p-2 rounded-full mb-4;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border); /* Added border for contrast */
  color: var(--color-text-muted);
  transition: all 0.3s ease;
}

.personal-modal-close:hover {
  background: var(--color-electric-purple);
  color: white;
  transform: rotate(90deg);
}

/* Internal Grid for Personal Modal */
.bento--personal-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: minmax(140px, auto);
  gap: 12px;
}

@media (min-width: 1024px) {
  .bento--personal-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .personal-modal-content,
.modal-fade-leave-active .personal-modal-content {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-fade-enter-from .personal-modal-content {
  transform: scale(0.9) translateY(20px);
}

.modal-fade-leave-to .personal-modal-content {
  transform: scale(0.95) translateY(-10px);
}

/* Note: we removed .bento--personal accordion styles since it's now a modal */

/* Cierre instantáneo cuando se detecta por scroll */
.bento--personal.is-closing-instant {
  transition: none !important;
  max-height: 0 !important;
  opacity: 0 !important;
}

/* -------------------------------------------------
   Watermark – “WHO AM I”
   ------------------------------------------------- */
.about__watermark {
  @apply absolute inset-0 flex items-center justify-center pointer-events-none;
  font-family: var(--font-madi, 'Ms Madi', cursive);
  /* font-family: 'Playfair Display', serif; */
  font-size: 12rem; /* gigantic size */
  color: var(--color-electric-purple);
  opacity: 0.1; /* clearly visible but subtle */
  user-select: none;
  text-transform: lowercase;
  line-height: 1;
  mix-blend-mode: normal;
  z-index: 0; /* behind the grid */
}

@media (max-width: 640px) {
  .about__watermark {
    font-size: 6rem;
  }
}
</style>
